import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type AllocationTargetReport } from '@greencandle/api-client'
import { api } from '../../api'
import { Failed, num, toneOf } from '../primitives'
import { BADGE_FOR_CLASS } from '../dictionaries'

/**
 * UI-1 (EPIC 4): strategic allocation - target weights per class, and the drift that makes them
 * a control rather than a note.
 */

export const CLASSES = ['STOCK', 'ETF', 'OPTION', 'CRYPTO', 'CASH', 'CUSTOM'] as const

/**
 * UI-1. Target weights per asset class, and the drift alert that makes them a control.
 *
 * <p>FR-E4 has computed the actual weights since the beginning; a target is the other half, and
 * without it the screen can only say what the portfolio <em>is</em>. A portfolio rarely goes off
 * plan by being rebalanced badly - it goes off plan by one holding running while nobody looks,
 * and only a stated intention with a corridor around it catches that.
 *
 * <p>The corridor is five <b>points</b>, not five per cent of the target. At the small end that
 * is the difference between a rule and a nuisance: a 5% crypto target sitting at 8% is 60% off
 * relatively, and three points off in the way that matters.
 */
export function TargetsPanel({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Record<string, string>>({})

  const plan = useQuery({
    queryKey: ['targets', accountId],
    queryFn: () => api.allocationTargets(accountId),
  })

  const save = useMutation({
    mutationFn: (next: Record<string, string>) => api.setAllocationTargets(accountId, next),
    onSuccess: () => {
      setEditing(false)
      void queryClient.invalidateQueries({ queryKey: ['targets', accountId] })
    },
  })

  const report: AllocationTargetReport | undefined = plan.data

  function beginEditing() {
    const current: Record<string, string> = {}
    for (const row of report?.rows ?? []) {
      if (row.target != null) current[row.assetClass] = String(row.target)
    }
    setDraft(current)
    setEditing(true)
  }

  const draftTotal = Object.values(draft)
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n))
    .reduce((a, b) => a + b, 0)

  return (
    <section className="panel">
      <div className="panel-header">
        <h3>Strategic asset allocation</h3>
        <span className="panel-note">
          {report?.available
            ? `Target vs actual · ±${num(report.corridor)} point corridor`
            : 'UI-1 · target vs actual'}
        </span>
      </div>

      <div className="controls-box">
        <div className="control-group control-end">
          <button
            type="button"
            className="btn-primary"
            onClick={() => (editing ? save.mutate(draft) : beginEditing())}
            disabled={save.isPending}
          >
            {save.isPending ? 'Saving…' : editing ? 'Save plan' : 'Set targets'}
          </button>
        </div>
        {editing && (
          <div className="control-group control-end">
            <button type="button" className="term-link" onClick={() => setEditing(false)}>
              cancel
            </button>
          </div>
        )}
        <div className="commit-note">
          {editing
            ? `Shares of the portfolio, in per cent. Currently ${draftTotal.toFixed(1)}% planned — a plan that does not add to 100 is allowed and reported, not refused.`
            : 'A target is only a control once something warns you it has been breached.'}
        </div>
      </div>

      {plan.isError && <Failed error={plan.error} />}
      {save.isError && <Failed error={save.error} />}

      {editing ? (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Asset class</th>
                <th className="right">Target %</th>
              </tr>
            </thead>
            <tbody>
              {CLASSES.map((assetClass) => (
                <tr key={assetClass}>
                  <td>
                    <span className={`badge ${BADGE_FOR_CLASS[assetClass] ?? 'badge-custom'}`}>
                      {assetClass}
                    </span>
                  </td>
                  <td className="right">
                    <input
                      type="text"
                      inputMode="decimal"
                      style={{ width: 90, textAlign: 'right' }}
                      aria-label={`${assetClass} target`}
                      value={draft[assetClass] ?? ''}
                      placeholder="—"
                      onChange={(e) => {
                        const next = { ...draft }
                        // An emptied field drops the class from the plan rather than setting it
                        // to zero: "no view on crypto" and "no crypto" are different statements.
                        if (e.target.value.trim() === '') delete next[assetClass]
                        else next[assetClass] = e.target.value.trim()
                        setDraft(next)
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !report ? (
        <p className="panel-foot">Loading…</p>
      ) : !report.available ? (
        <p className="panel-foot">{report.reason} Press “Set targets” to write one.</p>
      ) : (
        <>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Asset class</th>
                  <th className="right">Target</th>
                  <th className="right">Actual</th>
                  <th className="right">Drift</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row) => (
                  <tr key={row.assetClass}>
                    <td>
                      <span className={`badge ${BADGE_FOR_CLASS[row.assetClass] ?? 'badge-custom'}`}>
                        {row.assetClass}
                      </span>
                    </td>
                    <td className="right num">
                      {row.target == null ? (
                        <span className="gap-inline">unplanned</span>
                      ) : (
                        `${num(row.target)}%`
                      )}
                    </td>
                    <td className="right num">{num(row.actual)}%</td>
                    <td className={`right num ${toneOf(row.drift)}`}>
                      {Number(row.drift) > 0 ? '+' : ''}
                      {num(row.drift)}
                    </td>
                    <td>
                      {row.breached ? (
                        <span className="badge badge-custom">outside corridor</span>
                      ) : (
                        <span className="cell-sub">in range</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {report.planIncomplete && (
            <p className="panel-foot warn">
              The plan adds to {num(report.planTotal)}%, not 100%. Allowed, and worth knowing:
              drift is measured against what you wrote, so an incomplete plan understates it.
            </p>
          )}
          {report.holdingsValued < report.holdingsConsidered && (
            <p className="panel-foot warn">
              Actual weights cover {report.holdingsValued} of {report.holdingsConsidered}{' '}
              holdings. Drift measured over part of a book is not drift.
            </p>
          )}
        </>
      )}
    </section>
  )
}
